import React, { useEffect, useRef, useState } from 'react';
import type { EmailTemplate, TemplateContext } from './mailto.js';
import { openTemplateEmail } from './mailto.js';
import { downloadTemplateEml } from './eml.js';
import type { EmlDownloader } from './eml.js';
import { api } from '../api/client.js';
import { useAgentTeller } from '../agent-teller.js';
import {
  EMAIL_TEMPLATE_BUTTON_CLASS,
  EMAIL_TEMPLATE_TRIGGER_CLASS,
} from './styles.js';

/**
 * Best-effort logs an email-template download into Call History (CCF-63, #434).
 *
 * @dylan219 (#434, 2026-07-21): "when an agent clicks on an email template and
 * downloads it these need to be written into the call history." Fired AFTER the
 * `.eml`/`mailto:` draft is produced, and DELIBERATELY not awaited / never
 * throws — the log must never block or break the agent's download flow. When no
 * customer is in context the BFF simply acks `{ recorded: false }`. Mirrors the
 * identical logging on {@link EmailTemplateButtons}: the download must be
 * recorded whichever surface (button row or dropdown) the agent picks it from.
 */
function logTemplateDownload(
  template: EmailTemplate,
  context: TemplateContext,
  tellerId: string | undefined,
): void {
  void api
    .POST('/calls/template-event', {
      params: { header: { 'X-Teller-Id': tellerId ?? '' } },
      body: {
        customerNumber: context.cif ?? '',
        templateId: template.id,
        templateLabel: template.label,
      },
    })
    .catch(() => {
      /* best-effort: a logging failure must never break the download */
    });
}

/**
 * Top-right **dropdown selector** for a screen's email templates (#441).
 *
 * The visual complement to {@link EmailTemplateButtons}: instead of a row of
 * per-template buttons, it surfaces a single "Email templates ▾" trigger and
 * lists the templates in a dropdown. Choosing one pops the template as a draft
 * (prefilled from `context`) for the agent's default mail client (Outlook) —
 * a template that provides an HTML body ({@link EmailTemplate.buildHtml}) is
 * delivered as a downloadable, formatted `.eml` draft (#434), otherwise it
 * falls back to the plain-text `mailto:` draft.
 *
 * Modelled on {@link ProcessMenu} (same accessible dropdown pattern:
 * `aria-haspopup`/`aria-expanded`, close on outside-click). The draft itself is
 * client-side; choosing a template additionally fires a best-effort BFF call to
 * record the download in Call History (CCF-63, #434) — see
 * {@link logTemplateDownload}, identical to {@link EmailTemplateButtons}. The
 * download is never gated on that call. Reused by every screen that wants its
 * templates in the top-right rather than an inline button row.
 */
export function EmailTemplateMenu({
  templates,
  context,
  label = 'Email templates',
  opener,
  downloader,
}: {
  templates: readonly EmailTemplate[];
  context: TemplateContext;
  /** Trigger-button text shown before a selection is made. */
  label?: string;
  /** Injectable mailto opener (tests); defaults to `window.location.assign`. */
  opener?: (url: string) => void;
  /** Injectable `.eml` downloader (tests); defaults to the BFF-served download. */
  downloader?: EmlDownloader;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const tellerId = useAgentTeller();

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onClickOutside);
        document.removeEventListener('keydown', onKey);
      };
    }
    return undefined;
  }, [open]);

  if (templates.length === 0) return null;

  // #803 (@philipc39): when exactly ONE template is available, a dropdown
  // adds nothing — render it as a single button in the style of the
  // "Special category data" button on the Call History screen (solid orange,
  // envelope icon, template title as the accessible name) instead of a
  // trigger + one-item menu. Applies to every surface that routes through
  // this component (Statements and the postal-address change page today:
  // each carries only EDS Data Breach); surfaces with 2+ templates keep the
  // dropdown.
  if (templates.length === 1) {
    const single = templates[0]!;
    return (
      <div className="w-[260px]">
        <button
          type="button"
          data-template-id={single.id}
          onClick={() => choose(single)}
          aria-label={single.label}
          className={EMAIL_TEMPLATE_BUTTON_CLASS + ' w-full'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m2 7 10 6 10-6" />
          </svg>
          <span>{single.label}</span>
        </button>
      </div>
    );
  }

  function choose(t: EmailTemplate): void {
    setOpen(false);
    if (t.buildHtml) {
      downloadTemplateEml(t, context, downloader, tellerId);
    } else {
      openTemplateEmail(t, context, opener);
    }
    // Record the download in Call History (CCF-63, #434) — best-effort, never
    // awaited, never blocks the client-side download.
    logTemplateDownload(t, context, tellerId);
  }

  return (
    <div className="relative w-[260px]" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className={EMAIL_TEMPLATE_TRIGGER_CLASS}
      >
        <span>{label}</span>
        <svg
          className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <ul
          role="menu"
          aria-label={label}
          className="absolute top-full right-0 mt-2 w-full min-w-[260px] bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[280px] overflow-y-auto"
        >
          {templates.map((t) => (
            <li
              key={t.id}
              role="menuitem"
              tabIndex={0}
              data-template-id={t.id}
              onClick={() => choose(t)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  choose(t);
                }
              }}
              className="px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[14px] font-bold text-[#1d252c] hover:bg-[#fff3ee] focus:outline-none focus:bg-[#fdece4]"
            >
              {t.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
